import { isPolygon } from '@/modules/Editor/annotationTypes/polygon'
import { fromPolyBool, toPolyBool, polygonToCompoundPath } from '@/modules/Editor/compoundPath'
import { EditorEvents, ToolEvents } from '@/modules/Editor/eventBus'
import type { Action } from '@/modules/Editor/managers/actionManager'
import { polygon, segments, selectUnion } from '@/modules/Editor/polygonOperations'
import { combine, selectDifference } from '@/modules/Editor/polygonOperations'
import type { Annotation } from '@/modules/Editor/models/annotation/Annotation'
import { shallowCloneAnnotation } from '@/modules/Editor/models/annotation/cloneAnnotation'
import type { View } from '@/modules/Editor/views/view'
import { ToolName } from '@/modules/Editor/tools/types'

export const subtractPolygonsAction = (
  view: View,
  firstAnnotation: Annotation,
  secondAnnotation: Annotation,
): Action => {
  const previousSecondAnnotationData = secondAnnotation.data

  return {
    do(): boolean {
      if (!isPolygon(firstAnnotation.data) || !isPolygon(secondAnnotation.data)) {
        return false
      }

      const polygonCompoundPath1 = polygonToCompoundPath(firstAnnotation.data)
      const polygonCompoundPath2 = polygonToCompoundPath(secondAnnotation.data)
      const segment1 = selectUnion(
        combine(segments(toPolyBool(polygonCompoundPath1)), { segments: [], inverted: false }),
      )
      const segment2 = selectUnion(
        combine(segments(toPolyBool(polygonCompoundPath2)), { segments: [], inverted: false }),
      )

      const combined = combine(segment2, segment1)

      const subtractedSegment = selectDifference(combined)
      const subtractedPolygon = fromPolyBool(polygon(subtractedSegment))
      if (subtractedPolygon.path.length === 0) {
        EditorEvents.message.emit({
          content: 'You cannot delete entire annotations with subtractions',
          level: 'warning',
        })

        return false
      }

      const updatedAnnotation = shallowCloneAnnotation(secondAnnotation, {
        data: {
          ...secondAnnotation.data,
          path: subtractedPolygon.path,
          additionalPaths: subtractedPolygon.additionalPaths,
        },
      })

      ToolEvents.polygonUpdating.emit(
        { toolName: ToolName.Edit, lastHotkeyPressed: null, editing: false },
        [updatedAnnotation.id],
      )
      view.annotationManager.updateAnnotation(updatedAnnotation)

      return true
    },
    undo(): boolean {
      secondAnnotation.data = previousSecondAnnotationData
      view.annotationManager.updateAnnotation(secondAnnotation)
      return true
    },
  }
}
